import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMXN, fmtDate, fmtDateTime, daysFromNow, downloadBlob, todayStr } from "../lib/api";
import Topbar from "../components/Topbar";
import EmptyState from "../components/EmptyState";
import { Download, Send, CheckCircle2, AlertTriangle, Clock, Lock, Ban, Upload, FileText, X, Eye, EyeOff, Landmark } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";

const RECEIPT_MAX_BYTES = 10 * 1024 * 1024;
const RECEIPT_ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png"];

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const urgencyColor = (days) => {
  if (days === null) return "bg-slate-100 text-slate-600";
  if (days < 0) return "bg-red-100 text-red-700 border border-red-200";
  if (days <= 3) return "bg-amber-100 text-amber-800 border border-amber-200";
  return "bg-emerald-100 text-emerald-700 border border-emerald-200";
};

export default function Payments() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canPay = !!user?.can_create_payments;
  const canCancelPayment = user?.role === "corporate_admin" || user?.role === "treasury";
  const [tab, setTab] = useState("topay");
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [bankRef, setBankRef] = useState({});
  const [exporting, setExporting] = useState(false);
  const [toCancel, setToCancel] = useState(null); // payment queued for cancellation
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [payModal, setPayModal] = useState(null); // invoice being paid
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptDataUrl, setReceiptDataUrl] = useState("");
  const [payModalBankRef, setPayModalBankRef] = useState("");
  const [paySubmitting, setPaySubmitting] = useState(false);
  const isCorporateAdmin = user?.role === "corporate_admin";

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/payments/export`, { responseType: "blob" });
      downloadBlob(res.data, `pagos_${todayStr()}.xlsx`);
      toast.success(t("export.success"));
    } catch {
      toast.error(t("export.error"));
    } finally {
      setExporting(false);
    }
  };

  const load = async (withCancelled = showCancelled) => {
    const [iR, pR] = await Promise.all([
      api.get("/invoices"),
      api.get(`/payments${withCancelled ? "?include_cancelled=true" : ""}`),
    ]);
    setInvoices(iR.data);
    setPayments(pR.data);
  };

  useEffect(() => { load(); }, []);

  const toggleShowCancelled = () => {
    const next = !showCancelled;
    setShowCancelled(next);
    load(next);
  };

  const toPay = invoices.filter((i) => i.status === "validated" || i.status === "scheduled_for_payment");
  const sortedToPay = [...toPay].sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  // Fix WH79 (2026-10-15): facturas recibidas que todavía no se pueden pagar,
  // con lo que les falta. Antes no se veían en Pagos y parecía que no existían.
  const porValidar = invoices
    .filter((i) => i.status === "received" && !i.deleted_at)
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));

  const openPayModal = (inv) => {
    setPayModal(inv);
    setPayModalBankRef(bankRef[inv.id] || "");
    setReceiptFile(null);
    setReceiptDataUrl("");
  };

  const closePayModal = () => {
    if (paySubmitting) return;
    setPayModal(null);
    setReceiptFile(null);
    setReceiptDataUrl("");
  };

  const onReceiptSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!RECEIPT_ALLOWED_TYPES.includes(file.type)) {
      toast.error(t("payments.receiptInvalidType"));
      return;
    }
    if (file.size > RECEIPT_MAX_BYTES) {
      toast.error(t("payments.receiptTooLarge"));
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setReceiptFile(file);
      setReceiptDataUrl(dataUrl);
    } catch {
      toast.error(t("common.error"));
    }
  };

  const submitPay = async () => {
    if (!payModal || !receiptDataUrl) {
      toast.error(t("payments.receiptRequired"));
      return;
    }
    setPaySubmitting(true);
    try {
      await api.post(`/payments`, {
        invoice_id: payModal.id,
        amount: payModal.total,
        payment_date: new Date().toISOString(),
        payment_method: "transfer",
        bank_reference: payModalBankRef || undefined,
        receipt_filename: receiptFile?.name || "comprobante.pdf",
        receipt_data: receiptDataUrl,
      });
      toast.success(t("payments.executed", { amount: fmtMXN(payModal.total || 0) }));
      setPayModal(null);
      setReceiptFile(null);
      setReceiptDataUrl("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setPaySubmitting(false);
    }
  };

  const downloadReceipt = async (p) => {
    try {
      const res = await api.get(`/payments/${p.id}/receipt`, { responseType: "blob" });
      downloadBlob(res.data, p.receipt_filename || `comprobante-${p.id.slice(0, 8)}.pdf`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const exportCSV = () => {
    let csv = "Beneficiario,RFC,CLABE,Banco,Monto,UUID\n";
    sortedToPay.forEach((inv) => {
      csv += `${inv.supplier_name},${inv.supplier_rfc || ""},${inv.bank_clabe || ""},${inv.bank_name || ""},${inv.total},${inv.uuid}\n`;
    });
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `pagos_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    toast.success(t("payments.csvExported"));
  };

  const openCancel = (p) => {
    setToCancel(p);
    setCancelReason("");
  };

  const submitCancel = async () => {
    if (cancelReason.trim().length < 10) {
      toast.error(t("payments.cancelReasonMin"));
      return;
    }
    setCancelling(true);
    try {
      await api.post(`/payments/${toCancel.id}/cancel`, { reason: cancelReason.trim() });
      toast.success(t("payments.cancelSuccess"));
      setToCancel(null);
      setCancelReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <Topbar title={t("payments.title")} subtitle={t("payments.subtitle")} />
      <div className="p-6 space-y-5">
        {!canPay && (
          <div data-testid="payments-no-permission-banner" className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 flex items-center gap-2.5">
            <Lock className="w-4 h-4 text-slate-500" />
            {t("payments.noPermissionBanner")}
          </div>
        )}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1">
            <button onClick={() => setTab("topay")} data-testid="pay-tab-topay"
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition ${tab === "topay" ? "bg-[#1A1A1A] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {t("payments.tabToPay", { count: sortedToPay.length })}
            </button>
            <button onClick={() => setTab("pending")} data-testid="pay-tab-pending"
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition ${tab === "pending" ? "bg-[#1A1A1A] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {t("payments.tabPending", { count: porValidar.length })}
            </button>
            <button onClick={() => setTab("paid")} data-testid="pay-tab-paid"
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition ${tab === "paid" ? "bg-[#1A1A1A] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {t("payments.tabPaid", { count: payments.length })}
            </button>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {tab === "paid" && isCorporateAdmin && (
              <button data-testid="pay-toggle-cancelled-btn" onClick={toggleShowCancelled}
                className={`inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-xs font-bold border transition ${showCancelled ? "bg-red-50 border-red-200 text-red-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                {showCancelled ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {t("payments.showCancelled")}
              </button>
            )}
            <button data-testid="payments-export-btn" onClick={exportToExcel} disabled={exporting}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold disabled:opacity-50">
              <Download className="w-4 h-4" /> {exporting ? t("export.exporting") : t("export.button")}
            </button>
            {tab === "topay" && sortedToPay.length > 0 && (
              <button data-testid="pay-export-csv-btn" onClick={exportCSV}
                className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50 flex items-center gap-2">
                <Download className="w-4 h-4" /> {t("payments.exportCsv")}
              </button>
            )}
          </div>
        </div>

        {tab === "pending" ? (
          porValidar.length === 0 ? (
            <EmptyState title={t("payments.noPendingValidationTitle")} message={t("payments.pendingHint")} />
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="pay-pending-table">
              <div className="px-6 py-3 text-xs text-slate-500 border-b border-slate-100">{t("payments.pendingHint")}</div>
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("payments.supplier")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("payments.poInvoice")}</th>
                    <th className="text-right px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("payments.amount")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("payments.dueDate")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("payments.whatsMissing")}</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {porValidar.map((inv) => {
                    const conDiscrepancia = inv.price_mismatch && inv.price_review_status === "flagged";
                    const faltaRecepcion = inv.has_po === false && !inv.service_confirmation;
                    return (
                      <tr key={inv.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`pay-pending-row-${inv.id}`}>
                        <td className="px-6 py-3.5 text-sm font-semibold">{inv.supplier_name}</td>
                        <td className="px-6 py-3.5 font-mono text-xs text-slate-500">
                          {inv.has_po === false ? t("invoices.noPo") : inv.po_folio}{inv.folio_fiscal ? ` · ${inv.folio_fiscal}` : ""}
                        </td>
                        <td className="px-6 py-3.5 text-sm font-bold text-right">{fmtMXN(inv.total)}</td>
                        <td className="px-6 py-3.5 text-sm text-slate-500">{fmtDate(inv.due_date)}</td>
                        <td className="px-6 py-3.5">
                          <div className="flex flex-col gap-1">
                            {conDiscrepancia ? (
                              <span data-testid={`pay-pending-flagged-${inv.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200 text-[10px] font-bold w-fit">
                                <AlertTriangle className="w-3 h-3" /> {t("payments.pendingFlagged")}
                              </span>
                            ) : (
                              <span data-testid={`pay-pending-validate-${inv.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-bold w-fit">
                                <Clock className="w-3 h-3" /> {t("payments.pendingValidate")}
                              </span>
                            )}
                            {faltaRecepcion && (
                              <span data-testid={`pay-pending-reception-${inv.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 text-[10px] font-bold w-fit">
                                {t("payments.pendingReception")}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-3.5 text-right">
                          <button data-testid={`pay-pending-go-${inv.id}`} onClick={() => navigate(`/facturas?filtro=${conDiscrepancia ? "flagged" : "received"}`)}
                            className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-semibold hover:bg-slate-50">
                            {t("payments.reviewInInvoices")}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : tab === "topay" ? (
          sortedToPay.length === 0 ? (
            <EmptyState title={t("payments.noPendingTitle")} message={t("payments.allClearMsg")} />
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("payments.urgency")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("payments.supplier")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("payments.poInvoice")}</th>
                    <th className="text-right px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("payments.amount")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("payments.dueDate")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("payments.bank")}</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedToPay.map((inv, idx) => {
                    const days = daysFromNow(inv.due_date);
                    return (
                      <motion.tr key={inv.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.03 }}
                        className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                        <td className="px-6 py-3.5">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold ${urgencyColor(days)}`}>
                            {days < 0 ? <AlertTriangle className="w-3 h-3" /> : days <= 3 ? <Clock className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                            {days < 0 ? t("payments.overdue") : t("payments.daysShort", { n: days })}
                          </span>
                        </td>
                        <td className="px-6 py-3.5 text-sm font-semibold">{inv.supplier_name}</td>
                        <td className="px-6 py-3.5 font-mono text-xs text-slate-500">{inv.po_folio}</td>
                        <td className="px-6 py-3.5 text-sm font-bold text-right">{fmtMXN(inv.total)}</td>
                        <td className="px-6 py-3.5 text-sm text-slate-500">{fmtDate(inv.due_date)}</td>
                        <td className="px-6 py-3.5">
                          {inv.bank_account ? (
                            <div data-testid={`pay-bank-info-${inv.po_folio}`} className="text-xs">
                              <div className="font-semibold text-[#1A1A1A]">{inv.bank_name || "—"}</div>
                              <div className="text-slate-500 font-mono">{inv.bank_account}</div>
                            </div>
                          ) : (
                            <span data-testid={`pay-no-bank-${inv.po_folio}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                              <AlertTriangle className="w-3 h-3" /> {t("payments.noBankData")}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-3.5 text-right">
                          {canPay ? (
                            <div className="flex items-center gap-1.5 justify-end">
                              <input
                                data-testid={`pay-reference-input-${inv.po_folio}`}
                                type="text"
                                placeholder={t("payments.referencePlaceholder")}
                                value={bankRef[inv.id] || ""}
                                onChange={(e) => setBankRef({ ...bankRef, [inv.id]: e.target.value })}
                                className="w-28 px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs"
                              />
                              <button data-testid={`pay-execute-btn-${inv.po_folio}`} onClick={() => openPayModal(inv)}
                                className="btn-primary-chizy px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-50">
                                <Send className="w-3 h-3" /> {t("payments.pay")}
                              </button>
                            </div>
                          ) : (
                            <span data-testid={`pay-readonly-${inv.po_folio}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500">
                              <Lock className="w-3 h-3" /> {t("payments.readOnly")}
                            </span>
                          )}
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : (
          payments.length === 0 ? (
            <EmptyState title={t("payments.noPaidTitle")} message={t("payments.allClearMsg")} />
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.date")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("payments.supplier")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("payments.poInvoice")}</th>
                    <th className="text-right px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("payments.amount")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("payments.bank")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("payments.registeredBy")}</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} data-testid={`pay-row-${p.id}`} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 ${p.cancelled_at ? "bg-red-50/40" : ""}`}>
                      <td className="px-6 py-3.5 text-sm text-slate-500">{fmtDate(p.payment_date)}</td>
                      <td className="px-6 py-3.5 text-sm font-semibold">{p.supplier_name}</td>
                      <td className="px-6 py-3.5 font-mono text-xs text-slate-500">{p.po_folio}</td>
                      <td className={`px-6 py-3.5 text-sm font-bold text-right ${p.cancelled_at ? "text-red-700 line-through" : "text-emerald-700"}`}>{fmtMXN(p.amount)}</td>
                      <td className="px-6 py-3.5 text-sm">
                        <div>{p.bank}</div>
                        {/* Fix WH77 (2026-10-14): el pago ya aparecio en el archivo del banco (Pagos del banco) */}
                        {p.bank_confirmed_at && !p.cancelled_at && (
                          <span data-testid={`pay-bank-confirmed-${p.id}`} title={t("payments.bankConfirmedTitle", { date: fmtDate(p.bank_confirmed_at) })}
                            className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap">
                            <Landmark className="w-3 h-3" /> {t("payments.bankConfirmed")}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3.5 text-sm">
                        <div className="font-medium text-[#1A1A1A]">{p.executed_by_name || "—"}</div>
                        {p.cancelled_at && (
                          <div data-testid={`pay-cancelled-info-${p.id}`} className="mt-1.5 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1.5 space-y-0.5">
                            <div className="inline-flex items-center gap-1 font-bold">
                              <Ban className="w-3 h-3" /> {t("payments.cancelledBadge")}
                            </div>
                            <div>{t("payments.cancelledBy")}: <span className="font-semibold">{p.cancelled_by_name || "—"}</span> · {t("payments.cancelledAt", { date: fmtDateTime(p.cancelled_at) })}</div>
                            <div className="italic">{p.cancellation_reason}</div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-3.5 text-right">
                        <div className="flex items-center gap-1.5 justify-end">
                          {p.has_receipt ? (
                            <button data-testid={`pay-receipt-btn-${p.id}`} onClick={() => downloadReceipt(p)}
                              className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-[11px] font-bold flex items-center gap-1">
                              <Download className="w-3 h-3" /> {t("payments.receipt")}
                            </button>
                          ) : p.source === "archivo_banco" ? (
                            // Fix WH77 (2026-10-14): pago registrado desde el archivo del banco; su respaldo es el movimiento del banco
                            <span data-testid={`pay-receipt-bank-${p.id}`}
                              className="px-2.5 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap rounded-lg text-[11px] font-bold flex items-center gap-1">
                              <Landmark className="w-3 h-3" /> {t("payments.fromBankFile")}
                            </span>
                          ) : (
                            <button data-testid={`pay-receipt-btn-${p.id}`} disabled title={t("payments.noReceiptAttached")}
                              className="px-2.5 py-1.5 bg-slate-50 text-slate-400 border border-slate-200 rounded-lg text-[11px] font-bold flex items-center gap-1 cursor-not-allowed">
                              <Download className="w-3 h-3" /> {t("payments.noReceiptAttached")}
                            </button>
                          )}
                          {canCancelPayment && !p.cancelled_at && (
                            <button data-testid={`pay-cancel-btn-${p.id}`} onClick={() => openCancel(p)}
                              className="px-2.5 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg text-[11px] font-bold flex items-center gap-1">
                              <Ban className="w-3 h-3" /> {t("payments.cancelBtn")}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {payModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={closePayModal}>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="payment-pay-modal"
            className="bg-white rounded-2xl border border-slate-200 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-[#FFDF00]/30 text-[#1A1A1A] flex items-center justify-center">
                  <Send className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-display font-bold text-base text-[#1A1A1A]">{t("payments.payModalTitle")}</div>
                  <div className="text-xs text-slate-500 font-mono">{payModal.po_folio || payModal.folio_fiscal} · {fmtMXN(payModal.total)}</div>
                </div>
              </div>
              <button onClick={closePayModal} disabled={paySubmitting} className="text-slate-400 hover:text-slate-600" data-testid="payment-pay-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <label className="block text-xs font-bold text-slate-600 mb-1">{t("payments.referenceLabel")}</label>
            <input
              data-testid="payment-pay-modal-reference"
              type="text"
              value={payModalBankRef}
              onChange={(e) => setPayModalBankRef(e.target.value)}
              placeholder={t("payments.referencePlaceholder")}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#FFDF00]/50"
            />

            <label className="block text-xs font-bold text-slate-600 mb-1">{t("payments.receiptLabel")}</label>
            <label
              data-testid="payment-receipt-dropzone"
              className={`flex flex-col items-center justify-center gap-1.5 border-2 border-dashed rounded-xl px-4 py-5 text-center cursor-pointer transition mb-4 ${receiptFile ? "border-emerald-300 bg-emerald-50" : "border-slate-300 hover:border-slate-400 bg-slate-50"}`}
            >
              <input
                data-testid="payment-receipt-input"
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                className="hidden"
                onChange={onReceiptSelected}
              />
              {receiptFile ? (
                <>
                  <FileText className="w-6 h-6 text-emerald-600" />
                  <span className="text-xs font-semibold text-emerald-700">{receiptFile.name}</span>
                  <span className="text-[11px] text-emerald-600">{t("payments.receiptChangeCta")}</span>
                </>
              ) : (
                <>
                  <Upload className="w-6 h-6 text-slate-400" />
                  <span className="text-xs font-semibold text-slate-600">{t("payments.receiptUploadCta")}</span>
                  <span className="text-[11px] text-slate-400">{t("payments.receiptFormatsHint")}</span>
                </>
              )}
            </label>

            <div className="flex gap-2">
              <button onClick={closePayModal} disabled={paySubmitting}
                className="flex-1 py-2.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-xl text-sm font-bold disabled:opacity-50">
                {t("common.cancel")}
              </button>
              <button data-testid="payment-pay-modal-submit-btn" disabled={paySubmitting || !receiptDataUrl} onClick={submitPay}
                className="flex-1 py-2.5 btn-primary-chizy rounded-xl text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                <Send className="w-4 h-4" /> {paySubmitting ? t("payments.processing") : t("payments.pay")}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {toCancel && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !cancelling && setToCancel(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="payment-cancel-modal"
            className="bg-white rounded-2xl border border-slate-200 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center">
                <Ban className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold text-base text-[#1A1A1A]">{t("payments.cancelModalTitle")}</div>
                <div className="text-xs text-slate-500 font-mono">{toCancel.po_folio} · {fmtMXN(toCancel.amount)}</div>
              </div>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed mb-3">{t("payments.cancelModalIntro")}</p>
            <label className="block text-xs font-bold text-slate-600 mb-1">{t("payments.cancelReasonLabel")}</label>
            <textarea
              data-testid="payment-cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder={t("payments.cancelReasonPh")}
              rows={3}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-300 mb-3"
            />
            <div className="flex gap-2">
              <button onClick={() => setToCancel(null)} disabled={cancelling}
                className="flex-1 py-2.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-xl text-sm font-bold disabled:opacity-50">
                {t("common.cancel")}
              </button>
              <button data-testid="payment-cancel-submit-btn" disabled={cancelling || cancelReason.trim().length < 10} onClick={submitCancel}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                <Ban className="w-4 h-4" /> {t("payments.cancelBtn")}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}
